/*/=======================================================================================\*\
|| IMPORTS DE DEPENDENCIAS                                                                 ||
\*\=======================================================================================/*/
import { useState } from 'react';
import { ROUTES } from '../../const/routes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

/*/=======================================================================================\*\
|| IMPORTS DE COMPONENTES PROPIOS DEL PROYECTO                                             ||
\*\=======================================================================================/*/
import styles from './OlvidoContrasenia.module.css';
import BannerLogo from '../../components/BannerLogo/BannerLogo';
import ImagenTragamonedasInicio from '../../components/ImagenTragamonedasInicio/ImagenTragamonedasInicio';
import BotonGrandeDinamico from '../../components/Botones/BotonGrandeDinamico/BotonGrandeDinamico';
import Footer from '../../components/Footer/Footer';

/*/=======================================================================================\*\
|| PÁGINA OLVIDO CONTRASEÑA                                                                ||
\*\=======================================================================================/*/
const OlvidoContrasenia = () => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('forgotPassword.emailFormatInvalid'))
      .required(t('forgotPassword.emailRequired')),
  });

  const initialValues = {
    email: '',
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setSuccess(false);

    try {
      await axios.post(
        `${process.env.REACT_APP_DIRECCION}/api/auth/forgot-password`,
        { email: values.email },
        { withCredentials: true }
      );

      setSuccess(true);
      setTimeout(() => {
        navigate(ROUTES.resetContrasenia, { state: { email: values.email } });
      }, 2000);
    } catch (err) {
      if (err.response?.data?.msg) {
        setError(err.response.data.msg);
      } else {
        setError(t('forgotPassword.errorProcessing'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        'flex flex-col min-h-screen ' + styles.olvidoContraseniaContainer
      }
    >
      <div className="flex flex-col flex-grow items-center justify-between text-center px-4">
        <BannerLogo />

        <div className="flex flex-col lg:flex-row justify-center items-center lg:items-end w-full max-w-5xl gap-4 lg:gap-8 mb-4">
          <div className="w-full lg:w-auto flex justify-center order-1 lg:order-1">
            <ImagenTragamonedasInicio />
          </div>

          <div
            className={`p-5 lg:p-6 w-full max-w-md lg:max-w-xl space-y-3 flex flex-col bg-bgContenedorOscuro rounded-lg
                      border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro 
                      shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)] order-2 lg:order-2`}
          >
            <h2 className="text-xl text-yellow-500 font-bold mb-2">
              {t('forgotPassword.title')}
            </h2>

            <p className="text-sm text-gray-300 mb-2">
              {t('forgotPassword.subtitle1')}
              <br />
              {t('forgotPassword.subtitle2')}
            </p>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form>
                  <div className="mb-3">
                    <Field
                      type="email"
                      name="email"
                      className={
                        'w-full px-3 py-1.5 text-sm text-gray-900 bg-gray-200 rounded-lg border-2 focus:outline-none ' +
                        (errors.email && touched.email
                          ? 'border-red-500 focus:border-red-600'
                          : 'border-gray-300 focus:border-indigo-600')
                      }
                      placeholder={t('forgotPassword.emailPlaceholder')}
                    />
                    <ErrorMessage
                      name="email"
                      component="p"
                      className="text-red-400 text-xs mt-2"
                    />
                  </div>

                  <BotonGrandeDinamico
                    texto={isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submitButton')}
                    bgColor="green"
                    tipoBoton="submit"
                    disabled={isSubmitting}
                  />

                  <div className="mt-4 h-5">
                    {success && (
                      <p className="text-green-400 text-xs">
                        {t('forgotPassword.successMessage')}
                      </p>
                    )}
                    {error && <p className="text-red-400 text-xs">{error}</p>}
                  </div>
                </Form>
              )}
            </Formik>

            <button
              onClick={() => navigate(ROUTES.login)}
              className="text-blue-400 hover:underline text-sm"
            >
              {t('forgotPassword.backToLogin')}
            </button>
          </div>
        </div>
      </div>

      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
};
export default OlvidoContrasenia;
